import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import content from "@/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Testimonials() {
  const t = content.testimonials;
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);

  const go = (step) => {
    setDir(step);
    setIndex((prev) => (prev + step + t.items.length) % t.items.length);
  };

  const item = t.items[index];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="relative py-16 lg:py-24 bg-sand texture-grain">
      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE }}
          className="text-[11px] uppercase tracking-[0.3em] text-gold mb-6"
        >
          {t.eyebrow}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-serif font-light text-4xl sm:text-5xl leading-tight mb-16"
          data-testid="testimonials-heading"
        >
          {t.heading}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: EASE, delay: 0.2 }}
          className="relative"
        >
          <Quote size={40} className="mx-auto text-crimson/50 mb-8 rotate-180" />
          <div className="min-h-[220px] sm:min-h-[180px] flex items-center justify-center">
            <AnimatePresence mode="wait" custom={dir}>
              <motion.figure
                key={index}
                custom={dir}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -60 }}
                transition={{ duration: 0.6, ease: EASE }}
                data-testid="testimonial-card"
              >
                <blockquote className="font-serif font-light italic text-2xl sm:text-3xl lg:text-4xl leading-snug text-ink max-w-3xl mx-auto">
                  “{item.quote}”
                </blockquote>
                <figcaption className="mt-8">
                  <span className="text-sm font-medium tracking-wide">{item.author}</span>
                  <span className="mx-3 text-gold">·</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-fog">
                    {item.role}, {item.location}
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>

          <div className="mt-12 flex items-center justify-center gap-6">
            <button
              data-testid="testimonial-prev-btn"
              onClick={() => go(-1)}
              className="w-12 h-12 border border-ink/25 flex items-center justify-center hover:bg-ink hover:text-linen hover:border-ink transition-colors duration-300"
              aria-label="Previous testimonial"
            >
              <ArrowLeft size={16} />
            </button>
            <div className="flex gap-2">
              {t.items.map((_, i) => (
                <button
                  key={i}
                  data-testid={`testimonial-dot-${i}`}
                  onClick={() => { setDir(i > index ? 1 : -1); setIndex(i); }}
                  className={`h-1.5 transition-all duration-500 ${i === index ? "w-8 bg-gold" : "w-1.5 bg-ink/20 hover:bg-ink/40"}`}
                  aria-label={`Testimonial ${i + 1}`}
                />
              ))}
            </div>
            <button
              data-testid="testimonial-next-btn"
              onClick={() => go(1)}
              className="w-12 h-12 border border-ink/25 flex items-center justify-center hover:bg-ink hover:text-linen hover:border-ink transition-colors duration-300"
              aria-label="Next testimonial"
            >
              <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
